import { AxiosResponse } from "axios";

import { IApiErrorResponse } from "../@types/ApiError";
import {
  ITmpOnboardingCompleteRequest,
  ITmpOnboardingPrefill,
} from "../@types/TmpOnboarding";
import { ANONYMOUS_REQUEST, api } from "./api";

const ONBOARDING_URL = "/Portal/TransmuralPlatform/Onboarding";

/**
 * The one-time code travels in this header, never in the request path, so
 * request logs, Sentry transaction names and load-balancer logs never see it.
 */
export const TMP_ONBOARDING_CODE_HEADER = "X-Tmp-Onboarding-Code";

/** sessionStorage key holding the link after it is stripped from the address bar. */
const LINK_STORAGE_KEY = "tmp-onboarding-link";

/** The tenant regions a completion link can name; the API opens that region's database. */
export const TMP_REGIONS = ["EU", "US"] as const;
export type TTmpRegion = (typeof TMP_REGIONS)[number];

/** Normalises the `region` query value; null when it is not a region we serve. */
export function parseTmpRegion(value: string | null): TTmpRegion | null {
  const upper = value?.trim().toUpperCase();
  return (TMP_REGIONS as readonly string[]).includes(upper ?? "")
    ? (upper as TTmpRegion)
    : null;
}

/**
 * Transmural Platform onboarding completion (EN-5203). Both calls are
 * anonymous, pre-login requests: the hospital clinician arrives from TMP with
 * a one-time code and no portal session, so the stored bearer token (if any
 * staff member happens to be signed in on this browser) must not travel, and
 * a 401 must not bounce them to /login. ANONYMOUS_REQUEST also pins the call
 * to the default API — the code was minted there.
 *
 * Both calls carry the link's `region` query param: the anonymous endpoints
 * use it to open the right regional database, since there is no session to
 * derive it from. The code itself goes in the `X-Tmp-Onboarding-Code` header.
 *
 * The shared axios interceptor resolves non-2xx responses, so callers branch
 * on `status` (404 = unknown/expired/consumed code, 400 = validation).
 */
export const tmpOnboardingService = {
  async getPrefill(
    code: string,
    region: TTmpRegion
  ): Promise<AxiosResponse<ITmpOnboardingPrefill | IApiErrorResponse>> {
    return api.get<ITmpOnboardingPrefill | IApiErrorResponse>(ONBOARDING_URL, {
      ...ANONYMOUS_REQUEST,
      params: { region },
      headers: { [TMP_ONBOARDING_CODE_HEADER]: code },
    });
  },

  async complete(
    code: string,
    region: TTmpRegion,
    request: ITmpOnboardingCompleteRequest
  ): Promise<AxiosResponse<unknown>> {
    return api.put<unknown>(ONBOARDING_URL, request, {
      ...ANONYMOUS_REQUEST,
      params: { region },
      headers: { [TMP_ONBOARDING_CODE_HEADER]: code },
    });
  },
};

export interface ITmpOnboardingLink {
  code: string;
  region: TTmpRegion;
}

/**
 * The completion link is moved out of the address bar on first render; it is
 * kept for this tab only (sessionStorage) so a reload still finds it. Cleared
 * once the code is spent or rejected. Storage may be unavailable (private
 * mode, blocked site data): the page then works as before, minus the reload.
 */
export const tmpOnboardingLinkStore = {
  save(link: ITmpOnboardingLink): void {
    try {
      window.sessionStorage.setItem(LINK_STORAGE_KEY, JSON.stringify(link));
    } catch {
      // Storage unavailable: a reload will show the expired message.
    }
  },

  read(): ITmpOnboardingLink | null {
    try {
      const raw = window.sessionStorage.getItem(LINK_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ITmpOnboardingLink>;
      const region = parseTmpRegion(parsed.region ?? null);
      return typeof parsed.code === "string" && parsed.code && region
        ? { code: parsed.code, region }
        : null;
    } catch {
      return null;
    }
  },

  clear(): void {
    try {
      window.sessionStorage.removeItem(LINK_STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
  },
};
